import type { ReactNode } from 'react';

export const SUGGESTION_STATUSES = ['todo', 'done', 'ignored'] as const;

export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number];

export type SuggestionAction = {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
};

export type SuggestionItemProps = {
  title: string;
  status: SuggestionStatus;
  /** Passive `Tag`s qualifying the suggestion (resource kind, AI origin…). */
  tags?: ReactNode;
  /** One or two short lines of facts (file, provider, amount, date). */
  meta?: ReactNode;
  /** Details only useful while the suggestion is still to process; hidden once done or ignored. */
  children?: ReactNode;
  /** Label of the state tag once the suggestion has been applied. */
  doneLabel?: string;
  /** Decisive action creating the resource. Only shown while the suggestion is to process. */
  primaryAction: SuggestionAction;
  /** Low-commitment helper that stays available in every state (e.g. preview the file). */
  secondaryAction?: SuggestionAction;
  /** Ignore a pending suggestion, restore an ignored one, or reopen a done one. */
  onStatusToggle: () => void;
  'data-testid'?: string;
};

const DEFAULT_DONE_LABEL = 'Créé';

const TOGGLE_LABELS: Record<SuggestionStatus, string> = {
  todo: 'Ignorer',
  ignored: 'Rétablir',
  done: 'Marquer à faire',
};

export function useSuggestionItem(props: SuggestionItemProps) {
  const isTodo = props.status === 'todo';

  return {
    isTodo,
    toggleLabel: TOGGLE_LABELS[props.status],
    stateTag:
      props.status === 'done'
        ? { label: props.doneLabel ?? DEFAULT_DONE_LABEL, color: 'done' as const }
        : props.status === 'ignored'
          ? { label: 'Ignoré', color: 'default' as const }
          : null,
    showDetails: isTodo && Boolean(props.children),
  };
}
